/**
 * The Vapor colours, in one place.
 *
 * Until now every Vapor colour lived inline in `vaporTheme.ts`, repeated across
 * component `styleOverrides` — `#0090D1` alone appeared 18 times — while
 * `palette` itself was left at the stock Backstage values. The result was a
 * theme that overrode Material UI component by component instead of being
 * generated from a palette, which is also why none of it reached Backstage UI:
 * `@backstage/ui` reads CSS custom properties, and the official MUI-to-BUI
 * converter reads `theme.palette` — neither can see a `styleOverrides` block.
 *
 * These constants are the source for `palette` in `vaporTheme.ts`. The
 * `--bui-*` values in `vapor-bui.css` are the same colours expressed for
 * Backstage UI; the two files are kept in sync by hand, so a change here needs
 * the matching change there.
 */

/** Primary brand blue — buttons, links, indicators, focus rings. */
export const vaporBlue = '#0090D1';
/** Light end of the Vapor gradient `linear-gradient(315deg, #00C3EA, #0090D1)`. */
export const vaporBlueLight = '#00C3EA';
/** Dark blue — headings, pressed states, secondary. */
export const vaporBlueDark = '#005075';

/** Near-black used for body text and for the header and sidebar backgrounds. */
export const vaporInk = '#152935';
/** Muted text — table sub-labels, tab labels at rest. */
export const vaporInkSubtle = '#5A6872';

/** Raised surface: cards, table rows, content background. */
export const vaporSurface = '#FFFFFF';
/**
 * Recessed surface: selected tabs, hover tints, `--bui-bg-neutral-2`. Not the
 * page canvas — that is white, so the Backstage UI pages match the Material UI
 * ones, which `BackstageContent` already paints white.
 */
export const vaporSurfaceAlt = '#F2F5F8';

/** Subtle separator — row and tab dividers. */
export const vaporBorderLight = '#C9D9E8';
/** Stronger outline — surface borders, disabled foreground. */
export const vaporBorderStrong = '#9DA6AD';
/**
 * The neutral outline for *interactive* boundaries.
 *
 * WCAG 1.4.11 asks 3:1 of anything that delimits a control, and
 * `vaporBorderStrong` sits at 2.47:1 against white — fine as a decorative
 * separator, not enough as the edge of a button. This is the same grey walked
 * down until it clears the threshold (4.08:1), and it is what outlines the
 * Backstage UI secondary buttons, whose stock styling has no border at all and
 * would otherwise render white on the white canvas.
 */
export const vaporBorderContrast = '#767F86';

export const vaporSuccess = '#09822A';
export const vaporWarning = '#FDB927';
/**
 * The brand amber is unusable wherever contrast matters: `#FDB927` sits at
 * 1.73:1 against white. This is the darkened amber that clears WCAG AA in both
 * directions — 5.93:1 as text on white, and as a background carrying white
 * text. Used for warning text, for the warning Alert copy and for the warning
 * banner; the brand amber stays on fills.
 */
export const vaporWarningDark = '#8A5A00';
export const vaporError = '#D82829';
